import {
  getContractManagementContractFile,
  getContractFileContractTypeExtractions,
  getContractFileStandaloneExtractions,
  getContractManagementContractFileClauses,
  getContractManagementContractFileObligations,
  getContractManagementContractFileDates,
  getContractManagementContractFileSigningRequests,
  rerunContractFileExtractions,
  rerunContractFileExtraction,
  type ContractManagementContractFileInDbExtended,
  type ContractTypeExtractions,
  type ContractTypeExtractionEntry,
  type ContractManagementClauseInDb,
  type ContractManagementObligationInDb,
  type ContractManagementDateInDb,
  type SigningRequestWithRelationsInDb,
} from '@/api';

/**
 * Extract error message from various error types
 */
function extractErrorMessage(err: unknown, defaultMessage: string): string {
  if (err instanceof Error) {
    return err.message;
  }
  if (typeof err === 'object' && err !== null && 'detail' in err) {
    const detail = err.detail;
    return String(detail);
  }
  return defaultMessage;
}

export const ContractFileDetailService = {
  /**
   * Get contract file by UID
   */
  async getContractFile(uid: string): Promise<ContractManagementContractFileInDbExtended> {
    try {
      const response = await getContractManagementContractFile({
        path: { uid },
      });

      if (!response.data) {
        throw new Error('Failed to fetch contract file');
      }

      return response.data;
    } catch (error) {
      throw new Error(
        extractErrorMessage(error, 'Failed to fetch contract file. Please try again.'),
        { cause: error }
      );
    }
  },

  /**
   * Every extraction prompt of the contract's type, each with the revision's
   * extraction answering it (null while not asked yet). The current revision
   * unless one is named; an untyped contract has no results.
   */
  async getContractTypeExtractions(
    contractFileUid: string,
    revisionUid?: string | null
  ): Promise<ContractTypeExtractions> {
    try {
      const response = await getContractFileContractTypeExtractions({
        path: { uid: contractFileUid },
        query: revisionUid ? { revision_uid: revisionUid } : undefined,
      });

      if (!response.data) {
        throw new Error('Failed to fetch extractions');
      }

      return response.data;
    } catch (error) {
      throw new Error(
        extractErrorMessage(error, 'Failed to fetch extractions. Please try again.'),
        { cause: error }
      );
    }
  },

  /**
   * The standalone extraction prompts (not linked to any contract type, e.g. a
   * view's question column) the revision was asked, each with its extraction.
   * The current revision unless one is named.
   */
  async getStandaloneExtractions(
    contractFileUid: string,
    revisionUid?: string | null
  ): Promise<ContractTypeExtractionEntry[]> {
    try {
      const response = await getContractFileStandaloneExtractions({
        path: { uid: contractFileUid },
        query: revisionUid ? { revision_uid: revisionUid } : undefined,
      });

      if (!response.data) {
        throw new Error('Failed to fetch custom extractions');
      }

      return response.data;
    } catch (error) {
      throw new Error(
        extractErrorMessage(error, 'Failed to fetch custom extractions. Please try again.'),
        { cause: error }
      );
    }
  },

  /**
   * Ask the revision (the current one unless named) every question it is asked
   * today anew, replacing their extractions — human overwrites included. One
   * credit per question; questions still running are skipped.
   */
  async rerunExtractions(contractFileUid: string, revisionUid?: string | null): Promise<void> {
    try {
      await rerunContractFileExtractions({
        path: { uid: contractFileUid },
        query: revisionUid ? { revision_uid: revisionUid } : undefined,
        throwOnError: true,
      });
    } catch (error) {
      throw new Error(
        extractErrorMessage(error, 'Failed to rerun extractions. Please try again.'),
        { cause: error }
      );
    }
  },

  /**
   * Ask one extraction's question anew on its revision, replacing it (its human
   * overwrite included). Refused while it is still running. One credit.
   */
  async rerunExtraction(contractFileUid: string, extractionUid: string): Promise<void> {
    try {
      await rerunContractFileExtraction({
        path: { uid: contractFileUid, extraction_uid: extractionUid },
        throwOnError: true,
      });
    } catch (error) {
      throw new Error(
        extractErrorMessage(error, 'Failed to rerun the extraction. Please try again.'),
        { cause: error }
      );
    }
  },

  /**
   * The file's signing requests with their participants — only those whose
   * documents include the revision, when one is named. Empty for a user
   * without signing access.
   */
  async getSigningRequests(
    contractFileUid: string,
    revisionUid?: string | null
  ): Promise<SigningRequestWithRelationsInDb[]> {
    try {
      const response = await getContractManagementContractFileSigningRequests({
        path: { uid: contractFileUid },
        query: revisionUid ? { revision_uid: revisionUid } : undefined,
      });

      if (!response.data) {
        throw new Error('Failed to fetch signing requests');
      }

      return response.data;
    } catch (error) {
      throw new Error(
        extractErrorMessage(error, 'Failed to fetch signing requests. Please try again.'),
        { cause: error }
      );
    }
  },

  /**
   * Get obligations for a contract file
   */
  async getObligations(contractFileUid: string): Promise<ContractManagementObligationInDb[]> {
    try {
      const response = await getContractManagementContractFileObligations({
        path: { uid: contractFileUid },
      });

      if (!response.data) {
        throw new Error('Failed to fetch obligations');
      }

      return response.data;
    } catch (error) {
      throw new Error(
        extractErrorMessage(error, 'Failed to fetch obligations. Please try again.'),
        { cause: error }
      );
    }
  },

  /**
   * Get obligations scoped to a specific vault file revision
   */
  async getObligationsByRevision(
    contractFileUid: string,
    revisionUid: string
  ): Promise<ContractManagementObligationInDb[]> {
    try {
      const response = await getContractManagementContractFileObligations({
        path: { uid: contractFileUid },
        query: { revision_uid: revisionUid },
      });
      if (!response.data) throw new Error('Failed to fetch obligations for revision');
      return response.data;
    } catch (error) {
      throw new Error(
        extractErrorMessage(error, 'Failed to fetch obligations for revision. Please try again.'),
        { cause: error }
      );
    }
  },

  /**
   * Get clauses for a contract file
   */
  async getClauses(
    contractFileUid: string,
    revisionUid?: string | null
  ): Promise<ContractManagementClauseInDb[]> {
    try {
      const response = await getContractManagementContractFileClauses({
        path: { uid: contractFileUid },
        query: revisionUid ? { revision_uid: revisionUid } : undefined,
      });

      if (!response.data) {
        throw new Error('Failed to fetch clauses');
      }

      return response.data;
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Failed to fetch clauses. Please try again.'), {
        cause: error,
      });
    }
  },

  /**
   * Get dates for a contract file, optionally scoped to a vault file revision
   */
  async getDates(
    contractFileUid: string,
    revisionUid?: string | null
  ): Promise<ContractManagementDateInDb[]> {
    try {
      const response = await getContractManagementContractFileDates({
        path: { uid: contractFileUid },
        query: revisionUid ? { revision_uid: revisionUid } : undefined,
      });

      if (!response.data) {
        throw new Error('Failed to fetch dates');
      }

      return response.data;
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Failed to fetch dates. Please try again.'), {
        cause: error,
      });
    }
  },
};
